import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { apiFetch, ApiError, integratorMeQueryRetry, integratorMeQueryRetryDelay } from "@/lib/api";
import { clearRejectedHubViewAs, hubViewAsUserId } from "./hubViewAs";

/** Matches `ALLOWED_ROLE_ID` in `src/api/auth.ts` (integrator admin). */
export const INTEGRATOR_ADMIN_ROLE_ID = 1;

export type IntegratorHubModuleAccessLevel = "read" | "read_write" | "admin";

export interface MeResponse {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    avatarUrl: string | null;
    orgId: number;
    roleId: number;
    roleIds?: number[];
    /** Module keys allowed for this role; derived from `public.integrator_role_subapp`. Kept for clients that only read the list. */
    allowedIntegratorSubapps?: string[];
    /** Per-module read, write (operational mutations), or administrator (includes hub settings); authoritative for hub RBAC in the UI. */
    integratorSubappAccess?: Record<string, IntegratorHubModuleAccessLevel>;
    actualIntegratorAdmin?: boolean;
    viewAs?: { userId: string; displayName: string | null; email: string | null } | null;
  };
  actualIntegratorAdmin?: boolean;
  viewAs?: { userId: string; displayName: string | null; email: string | null } | null;
  insideSalesQuoteRestricted?: boolean;
  moduleNavGroupsByModule?: Record<
    string,
    { id: string; label: string; icon: string; pagePaths: string[] }[]
  >;
}

export type IntegratorMeUser = MeResponse["user"];

export function integratorMeRoleIds(user: IntegratorMeUser | undefined): number[] {
  if (!user) {
    return [];
  }
  if (user.roleIds && user.roleIds.length > 0) {
    return user.roleIds;
  }
  return [user.roleId];
}

export function meQueryKey(userId: string | null | undefined, viewAsUserId?: string | null): readonly [string, string, string] {
  return ["me", userId ?? "signed-out", viewAsUserId ?? "self"];
}

export function useIntegratorMe(): ReturnType<typeof useQuery<MeResponse, ApiError>> {
  const { session } = useAuth();
  const [viewAsUserId, setViewAsUserId] = useState(() => hubViewAsUserId());
  const query = useQuery<MeResponse, ApiError>({
    queryKey: meQueryKey(session?.user.id, viewAsUserId),
    queryFn: () => apiFetch<MeResponse>("/api/me"),
    enabled: Boolean(session),
    retry: integratorMeQueryRetry,
    retryDelay: integratorMeQueryRetryDelay,
  });
  const activeViewAs = query.data?.viewAs ?? query.data?.user.viewAs ?? null;
  useEffect(() => {
    if (!query.data || !viewAsUserId || activeViewAs) return;
    clearRejectedHubViewAs();
    setViewAsUserId(null);
  }, [activeViewAs, query.data, viewAsUserId]);
  return query;
}

export function isIntegratorAdmin(roleIdOrUser: number | IntegratorMeUser | undefined): boolean {
  if (typeof roleIdOrUser === "object" && roleIdOrUser !== null) {
    return integratorMeRoleIds(roleIdOrUser).includes(INTEGRATOR_ADMIN_ROLE_ID);
  }
  return roleIdOrUser === INTEGRATOR_ADMIN_ROLE_ID;
}

export function canUseHubViewAs(me: MeResponse | undefined): boolean {
  return me?.actualIntegratorAdmin === true || me?.user.actualIntegratorAdmin === true;
}

export function insideSalesQuoteRestricted(me: MeResponse | undefined): boolean {
  if (!me?.user) {
    return false;
  }
  if (me.insideSalesQuoteRestricted === true) {
    return true;
  }
  if (isIntegratorAdmin(me.user) && !me.viewAs && !me.user.viewAs) {
    return false;
  }
  if (me.insideSalesQuoteRestricted === false && !me.viewAs && !me.user.viewAs) {
    return false;
  }
  const access = me.user.integratorSubappAccess?.inside_sales;
  if (access === "admin") {
    return false;
  }
  if (access === "read" || access === "read_write") {
    return true;
  }
  return false;
}
